import { customizations } from '../state/app-state.js';
import type { RotationEngine, RotationItem } from '../sign/rotation-engine.js';
import type { SlotId } from '../types/manifest.js';

const SLOT_LABELS: Record<string, string> = {
  'logo': 'Logo',
  'default.personal-loan': 'Personal Loans',
  'default.business-credit': 'Business Line of Credit',
  'default.agricultural': 'Agricultural Loan',
  'triggered.tornado': 'Tornado Alert',
  'triggered.multiple_people': 'Multiple People',
  'triggered.single_female': 'Single Female',
  'triggered.single_male': 'Single Male',
  'news.us': 'US News',
  'news.local': 'Local News',
};

/** Slots that always appear as rows even when no engine item exists yet. */
const ALWAYS_VISIBLE: SlotId[] = [
  'logo',
  'default.personal-loan', 'default.business-credit', 'default.agricultural',
  'triggered.multiple_people', 'triggered.single_female', 'triggered.single_male',
  'triggered.tornado',
  'news.us', 'news.local',
];

/** Slots that don't participate in normal rotation: no checkbox, no timing inputs, no arrows. */
const NO_ROTATION_CONTROLS = new Set<string>(['logo', 'triggered.tornado']);

/** Slots that may show timing controls only after an asset has been generated. */
const LAZY_TIMING = new Set<string>(['news.us', 'news.local']);

const COLLAPSE_KEY = 'sgt:campaign-collapsed';

export type EditOpener = (slotId: SlotId) => void;

export function renderCampaignEditor(host: HTMLElement, engine: RotationEngine, onEdit: EditOpener): void {
  const collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  host.innerHTML = `
    <div class="panel campaign-panel${collapsed ? ' is-folded' : ''}">
      <button type="button" class="campaign-panel__header" aria-expanded="${collapsed ? 'false' : 'true'}">
        <h2>Campaign</h2>
        <span class="campaign-panel__chevron" aria-hidden="true"></span>
      </button>
      <div class="campaign-panel__body">
        <ul class="campaign-list" id="ce-list"></ul>
      </div>
    </div>`;

  const panel = host.querySelector<HTMLDivElement>('.campaign-panel')!;
  const headerBtn = host.querySelector<HTMLButtonElement>('.campaign-panel__header')!;
  const list = host.querySelector<HTMLUListElement>('#ce-list')!;

  headerBtn.addEventListener('click', () => {
    const nowCollapsed = !panel.classList.contains('is-folded');
    panel.classList.toggle('is-folded', nowCollapsed);
    headerBtn.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
    localStorage.setItem(COLLAPSE_KEY, nowCollapsed ? '1' : '0');
  });

  function rebuild(): void {
    const engineItems = (engine as unknown as { items: RotationItem[] }).items;
    const byId = new Map<string, RotationItem>(engineItems.map((i) => [i.id, i]));

    list.innerHTML = '';
    for (const slotId of ALWAYS_VISIBLE) {
      const item = byId.get(slotId) ?? null;
      const customName = customizations.get().slots[slotId]?.name;
      const label = customName ?? SLOT_LABELS[slotId] ?? slotId;
      const li = document.createElement('li');
      li.dataset.id = slotId;
      li.append(rowFragment(slotId, label, item));
      list.append(li);
    }
  }

  function rowFragment(slotId: string, label: string, item: RotationItem | null): DocumentFragment {
    const tpl = document.createElement('template');
    const noControls = NO_ROTATION_CONTROLS.has(slotId);
    const lazy = LAZY_TIMING.has(slotId);

    const checkboxCell = noControls
      ? `<span class="campaign-list__placeholder">&mdash;</span>`
      : (lazy && !item)
        ? `<span class="campaign-list__placeholder">&mdash;</span>`
        : `<input type="checkbox" ${item?.enabled ? 'checked' : ''} aria-label="Enable ${escapeAttr(label)}" />`;

    const displayInput = (noControls || (lazy && !item))
      ? `<span class="campaign-list__placeholder">&mdash;</span>`
      : `<input type="number" min="500" max="60000" step="500" value="${item?.displayMs ?? ''}" title="Display ms" />`;

    const transitionInput = (noControls || (lazy && !item))
      ? `<span class="campaign-list__placeholder">&mdash;</span>`
      : `<input type="number" min="0" max="3000" step="50" value="${item?.transitionMs ?? ''}" title="Transition ms" />`;

    const arrows = noControls
      ? `<span class="campaign-list__placeholder">&nbsp;</span><span class="campaign-list__placeholder">&nbsp;</span>`
      : `<button type="button" class="arrow up" title="Move up">&#x25B2;</button><button type="button" class="arrow down" title="Move down">&#x25BC;</button>`;

    tpl.innerHTML = `
      ${checkboxCell}
      <span class="campaign-list__label">${escapeHtml(label)}</span>
      ${displayInput}
      ${transitionInput}
      ${arrows}
      <button type="button" class="campaign-list__edit button" data-action="edit" title="Edit">Edit</button>
    `;
    return tpl.content;
  }

  list.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    const li = target.closest('li');
    if (!li) return;
    const id = li.dataset.id!;
    if (target.type === 'checkbox') {
      target.checked ? engine.enable(id) : engine.disable(id);
    } else if (target.type === 'number') {
      const inputs = li.querySelectorAll<HTMLInputElement>('input[type=number]');
      if (inputs.length === 2) {
        const d = Number(inputs[0]!.value);
        const t = Number(inputs[1]!.value);
        if (Number.isFinite(d)) engine.setDisplayMs(id, d);
        if (Number.isFinite(t)) engine.setTransitionMs(id, t);
      }
    }
  });

  list.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const editBtn = target.closest<HTMLButtonElement>('button[data-action=edit]');
    if (editBtn) {
      const li = editBtn.closest('li');
      if (li) onEdit(li.dataset.id! as SlotId);
      return;
    }
    const arrowBtn = target.closest<HTMLButtonElement>('button.arrow');
    if (arrowBtn) {
      const li = arrowBtn.closest('li');
      if (!li) return;
      const ids = [...list.children].map((c) => (c as HTMLElement).dataset.id!);
      const i = ids.indexOf(li.dataset.id!);
      if (i < 0) return;
      const swapWith = arrowBtn.classList.contains('up') ? i - 1 : i + 1;
      if (swapWith < 0 || swapWith >= ids.length) return;
      [ids[i], ids[swapWith]] = [ids[swapWith]!, ids[i]!];
      engine.reorder(ids);
      rebuild();
    }
  });

  rebuild();
  customizations.subscribe(() => rebuild());
  setInterval(rebuild, 1500);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

function escapeAttr(s: string): string { return escapeHtml(s); }
